import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, tap, withLatestFrom } from 'rxjs';
import * as ChatSocketActions from './websocket.actions';
import * as ChannelActions from '../channel/channel.actions';
import * as MeetingActions from '../meeting/meeting.actions';
import * as TaskActions from '../task/task.actions';
import type { TaskNotification } from '../task/task.models';
import * as UserActions from '../user/user.actions';
import { WebSocketService } from '../../core/services';
import { OutboundEvent } from './websocket.models';
import { User } from '../user/user.models';
import { selectUser } from '../user/user.selectors';

@Injectable()
export class WebSocketEffects {
  private readonly actions$ = inject(Actions);
  private readonly websocketService = inject(WebSocketService);
  private readonly store = inject(Store);

  connectSocket$ = createEffect(
  () =>
    this.actions$.pipe(
      ofType(ChatSocketActions.connectSocket),
      tap(({ url, token }) => {
        this.websocketService.connect(url, token);
      })
    ),
  { dispatch: false }
);

  socketOpen$ = createEffect(() =>
    this.websocketService.open$.pipe(
      map(() => ChatSocketActions.connectSocketSuccess())
    )
  );

  socketClose$ = createEffect(() =>
    this.websocketService.close$.pipe(
      map(() => ChatSocketActions.socketClosed())
    )
  );

  socketError$ = createEffect(() =>
    this.websocketService.error$.pipe(
      map((error) => ChatSocketActions.receiveSocketError({ error }))
    )
  );

  receiveSocketEvents$ = createEffect(() =>
    this.websocketService.events$.pipe(
      map((event) => ChatSocketActions.receiveSocketEvent({ event }))
    )
  );

  handleSocketEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatSocketActions.receiveSocketEvent),
      map(({ event }) => this.mapOutboundEventToAction(event))
    )
  );

  handleChannelCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatSocketActions.receiveChannelCreated),
      mergeMap(({ channelCreated }) => [
        ChannelActions.ChannelFetchRequest(),
        ChatSocketActions.subscribeChannel({ channelId: channelCreated.channel_id }),
      ])
    )
  );

  handleAppEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatSocketActions.receiveAppEvent),
      withLatestFrom(this.store.select(selectUser)),
      map(([{ appEvent }, currentUser]) => {
        switch (appEvent.name) {
          case 'user.profile.updated': {
            const user = appEvent.payload['user'] as User | undefined;
            if (!user?.id) {
              return ChatSocketActions.receiveSocketError({
                error: 'Invalid user.profile.updated payload',
              });
            }

            return UserActions.UserProfilePatched({ user });
          }

          case 'meeting.direct_call_ready': {
            const targetUserId = appEvent.payload['target_user_id'];
            const channelId = appEvent.payload['channel_id'];
            const launchUrl = appEvent.payload['launch_url'];
            const mode = appEvent.payload['mode'];

            if (typeof targetUserId !== 'string' || typeof channelId !== 'string' || typeof launchUrl !== 'string') {
              return ChatSocketActions.receiveSocketError({
                error: 'Invalid meeting.direct_call_ready payload',
              });
            }

            if (targetUserId !== currentUser?.id) {
              return ChatSocketActions.ignoreSocketEvent();
            }

            return MeetingActions.MeetingReceiveLaunchSignal({
              launch: {
                channel_id: channelId,
                launch_url: launchUrl,
                mode: mode === 'audio' || mode === 'video' ? mode : undefined,
              },
            });
          }

          case 'meeting.group_call_invited': {
            const targetUserId = appEvent.payload['target_user_id'];
            const channelId = appEvent.payload['channel_id'];
            const launchUrl = appEvent.payload['launch_url'];
            const startedByUserId = appEvent.payload['started_by_user_id'];
            const startedByName = appEvent.payload['started_by_name'];
            const mode = appEvent.payload['mode'];
            const providerType = appEvent.payload['provider_type'];
            const roomName = appEvent.payload['room_name'];

            if (
              typeof targetUserId !== 'string'
              || typeof channelId !== 'string'
              || typeof launchUrl !== 'string'
              || typeof startedByUserId !== 'string'
            ) {
              return ChatSocketActions.receiveSocketError({
                error: 'Invalid meeting.group_call_invited payload',
              });
            }

            if (targetUserId !== currentUser?.id) {
              return ChatSocketActions.ignoreSocketEvent();
            }

            return MeetingActions.MeetingReceiveIncomingCall({
              incomingCall: {
                organization_id: appEvent.organization_id,
                channel_id: channelId,
                started_by_user_id: startedByUserId,
                started_by_name: typeof startedByName === 'string' && startedByName.trim() ? startedByName : 'Someone',
                mode: mode === 'audio' || mode === 'video' ? mode : 'video',
                provider_type: typeof providerType === 'string' ? providerType : '',
                room_name: typeof roomName === 'string' ? roomName : '',
                room_url: launchUrl,
                created_at: appEvent.created_at,
              },
            });
          }

          case 'meeting.direct_call_declined': {
            const targetUserId = appEvent.payload['target_user_id'];
            const channelId = appEvent.payload['channel_id'];

            if (typeof targetUserId !== 'string' || typeof channelId !== 'string') {
              return ChatSocketActions.receiveSocketError({
                error: 'Invalid meeting.direct_call_declined payload',
              });
            }

            if (targetUserId !== currentUser?.id) {
              return ChatSocketActions.ignoreSocketEvent();
            }

            return MeetingActions.MeetingReceiveDeclineSignal({ channelId });
          }

          case 'meeting.direct_call_ended': {
            const targetUserId = appEvent.payload['target_user_id'];
            const channelId = appEvent.payload['channel_id'];

            if (typeof targetUserId !== 'string' || typeof channelId !== 'string') {
              return ChatSocketActions.receiveSocketError({
                error: 'Invalid meeting.direct_call_ended payload',
              });
            }

            if (targetUserId !== currentUser?.id) {
              return ChatSocketActions.ignoreSocketEvent();
            }

            return MeetingActions.MeetingReceiveEndSignal({ channelId });
          }

          case 'task.notification.created': {
            const notification = appEvent.payload['notification'] as TaskNotification | undefined;
            if (!notification?.id || !notification.user_id) {
              return ChatSocketActions.receiveSocketError({
                error: 'Invalid task.notification.created payload',
              });
            }

            if (notification.user_id !== currentUser?.id) {
              return ChatSocketActions.ignoreSocketEvent();
            }

            return TaskActions.TaskNotificationReceived({ notification });
          }

          case 'task.project.changed': {
            const projectId = appEvent.payload['project_id'];
            const taskId = appEvent.payload['task_id'];

            if (typeof projectId !== 'string' || typeof taskId !== 'string') {
              return ChatSocketActions.receiveSocketError({
                error: 'Invalid task.project.changed payload',
              });
            }

            return TaskActions.TaskExternalChangeReceived({ projectId, taskId });
          }

          default:
            return ChatSocketActions.ignoreSocketEvent();
        }
      })
    )
  );

  handleIncomingCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatSocketActions.receiveIncomingCall),
      map(({ incomingCall }) => MeetingActions.MeetingReceiveIncomingCall({ incomingCall }))
    )
  );

  subscribeChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatSocketActions.subscribeChannel),
        tap(({ channelId }) => {
          this.websocketService.send({
            action: 'subscribe',
            channel_id: channelId,
          });
        })
      ),
    { dispatch: false }
  );

  unsubscribeChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatSocketActions.unsubscribeChannel),
        tap(({ channelId }) => {
          this.websocketService.send({
            action: 'unsubscribe',
            channel_id: channelId,
          });
        })
      ),
    { dispatch: false }
  );

  sendMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatSocketActions.sendChannelMessage),
        tap(
          ({
            channelId,
            clientMessageId,
            message,
            messageType,
            attributes,
          }) => {
            this.websocketService.send({
              action: 'send_message',
              channel_id: channelId,
              client_message_id: clientMessageId,
              message,
              message_type: messageType,
              attributes,
            });
          }
        )
      ),
    { dispatch: false }
  );

  sendTypingStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatSocketActions.sendTypingStatus),
        tap(({ channelId, isTyping }) => {
          this.websocketService.send({
            action: 'typing',
            channel_id: channelId,
            attributes: {
              state: isTyping ? 'start' : 'stop',
            },
          });
        })
      ),
    { dispatch: false }
  );

  markPendingOnSend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatSocketActions.sendChannelMessage),
      map((action) =>
        ChatSocketActions.markPendingMessage({
          clientMessageId: action.clientMessageId,
          channelId: action.channelId,
          message: action.message,
          messageType: action.messageType,
          attributes: action.attributes,
          createdAt: new Date().toISOString(),
        })
      )
    )
  );

  private mapOutboundEventToAction(event: OutboundEvent) {
    switch (event.event) {
      case 'message':
      case 'presence':
        if (event.message) {
          return ChatSocketActions.receiveChannelMessage({
            message: event.message,
          });
        }
        return ChatSocketActions.receiveSocketError({
          error: 'Received empty message event',
        });

      case 'presence_snapshot':
        if (event.presence_snapshot?.channel_id) {
          return ChatSocketActions.receivePresenceSnapshot({
            snapshot: event.presence_snapshot,
          });
        }
        return ChatSocketActions.receiveSocketError({
          error: 'Invalid presence snapshot payload',
        });

      case 'typing':
        if (event.message?.channel_id) {
          return ChatSocketActions.receiveTypingStatus({
            message: event.message,
          });
        }
        return ChatSocketActions.receiveSocketError({
          error: 'Invalid typing payload',
        });

      case 'app_event':
        if (event.app_event?.name) {
          return ChatSocketActions.receiveAppEvent({
            appEvent: event.app_event,
          });
        }
        return ChatSocketActions.receiveSocketError({
          error: 'Invalid app event payload',
        });

      case 'channel_created':
        if (event.channel_created?.channel_id) {
          return ChatSocketActions.receiveChannelCreated({
            channelCreated: event.channel_created,
          });
        }
        return ChatSocketActions.receiveSocketError({
          error: 'Invalid channel created payload',
        });

      case 'incoming_call':
        if (event.incoming_call?.channel_id) {
          return ChatSocketActions.receiveIncomingCall({
            incomingCall: event.incoming_call,
          });
        }
        return ChatSocketActions.receiveSocketError({
          error: 'Invalid incoming call payload',
        });

      case 'subscribed':
        if (event.message?.channel_id) {
          return ChatSocketActions.subscribeChannelSuccess({
            channelId: event.message.channel_id,
          });
        }
        return ChatSocketActions.receiveSocketError({
          error: 'Invalid subscribed event payload',
        });

      case 'unsubscribed':
        if (event.message?.channel_id) {
          return ChatSocketActions.unsubscribeChannelSuccess({
            channelId: event.message.channel_id,
          });
        }
        return ChatSocketActions.receiveSocketError({
          error: 'Invalid unsubscribed event payload',
        });

      case 'error':
        return ChatSocketActions.receiveSocketError({
          error: event.error || 'Unknown websocket error',
        });

      default:
        return ChatSocketActions.receiveSocketError({
          error: `Unhandled websocket event: ${event.event}`,
        });
    }
  }
}
